import { ChangeEvent, forwardRef } from "react";

import { ArrowUpTailIcon, CirclePlusIcon } from "@/assets/icons";
import Tooltip from "@/components/ui/tooltip";

type Props = {
  value: string;
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
};

/**
 * Presentational composer for the Agentic AI prompt.
 *
 * Renders no `<form>` of its own — it is mounted inside the host form, which
 * provides one, so the send button submits through that form and the Enter key
 * works natively.
 */
const AgenticAIComposer = forwardRef<HTMLInputElement, Props>(
  ({ value, placeholder, disabled, onChange }, ref) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    };

    const canSubmit = !disabled && value.trim().length > 0;

    return (
      <div className="flex w-full items-center gap-3 rounded-full border border-gainsboro bg-white px-4 py-2.5 focus-within:border-teal-300">
        <Tooltip
          content="Attaching files is not available yet"
          position="top-center"
        >
          <span className="flex">
            <button
              type="button"
              disabled
              aria-label="Attach a file"
              className="flex cursor-not-allowed items-center [&>svg]:h-6 [&>svg]:w-6 [&>svg]:fill-teal-500"
            >
              <CirclePlusIcon />
            </button>
          </span>
        </Tooltip>

        <input
          ref={ref}
          type="text"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          aria-label="Ask Agentic AI a question"
          className="min-w-0 flex-1 bg-transparent text-sm text-txtHeading outline-none placeholder:text-txtPlaceholder disabled:cursor-not-allowed focus-visible:outline-none"
        />

        <button
          type="submit"
          disabled={!canSubmit}
          aria-label="Send prompt"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-teal-500 disabled:bg-neutral-200 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:fill-white"
        >
          <ArrowUpTailIcon />
        </button>
      </div>
    );
  }
);

AgenticAIComposer.displayName = "AgenticAIComposer";

export default AgenticAIComposer;
